<script setup lang="ts">
  import type { AgencyNode } from '@gat-ship/types'
  import { computed, type HTMLAttributes } from 'vue'
  import {
    cn,
    FlagIcon,
    TooltipHint,
    useModalStack
  } from '@gat-ship/vue-components'
  import { IconChevronRight, IconLoading } from '@gat-ship/icons'
  import { inject } from 'vue'
  import { agencyStructureKey } from '@gat-ship/types/injection-keys'
  import { testIds } from '@gat-ship/types'
  import { Badge } from '@gat-ship/vue-components'
  import { agencyNodeSelectKey } from './agency-node-nav'

  const props = defineProps<{
    agencyNode: AgencyNode
    class?: HTMLAttributes['class']
  }>()

  const agencyStructureProvider = inject(agencyStructureKey)

  const isLoading = computed(() => {
    return agencyStructureProvider?.isLoading.agencyStructure
  })

  const _title = computed(() => {
    return props.agencyNode.name
  })

  const _description = computed(() => {
    return undefined
  })

  // Optional host override (the AdministrationModal panel opens the detail
  // modal); when unset, fall back to opening the agency detail modal directly
  // via the central stack (the routed structure view was retired).
  const onSelect = inject(agencyNodeSelectKey, null)

  const stack = useModalStack()
  function onAgencyNodeClicked() {
    if (onSelect) {
      onSelect(props.agencyNode.id)
      return
    }
    stack.open('agency', { id: props.agencyNode.id })
  }
</script>

<template>
  <div
    :class="
      cn(
        'group/agency flex min-h-16 grow cursor-pointer items-center bg-neutral-50 px-4 py-4 hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-700',
        props.class
      )
    "
    @click.stop="onAgencyNodeClicked()"
  >
    <div class="flex grow items-center justify-center gap-2">
      <div class="flex grow flex-row gap-1">
        <FlagIcon
          :flag="props.agencyNode.contactInfo.country.code"
          class="mr-3 size-6 self-center rounded-full"
        />
        <div class="mb-px flex grow flex-col gap-1">
          <div
            class="text-[13px] font-medium leading-none text-neutral-800 dark:text-neutral-300"
          >
            <div
              class="inline-flex w-fit min-w-0 grow-0 items-center gap-1 sm:items-end"
              :data-testid="testIds.agency.listName(props.agencyNode.id)"
            >
              {{ _title
              }}<TooltipHint v-if="_description" :text="_description" />
            </div>
          </div>
          <div
            class="text-[13px] leading-none text-neutral-500 dark:text-neutral-400"
          >
            <template v-if="isLoading"> Pending... </template>
            <template v-else>
              {{ props.agencyNode.shortName }}
            </template>
          </div>
        </div>
      </div>
      <div class="wrap flex flex-row items-center gap-1">
        <badge v-if="props.agencyNode.isInactive" variant="secondary">
          Inactive
        </badge>
        <div
          class="flex h-full w-5 shrink-0 items-center justify-center text-neutral-400 group-hover/agency:text-neutral-600"
        >
          <IconLoading v-if="isLoading" class="size-5" :is-loading="true" />
          <icon-chevron-right
            v-else
            class="size-5 transition duration-200 ease-out group-hover/agency:translate-x-1"
          />
        </div>
      </div>
    </div>
  </div>
</template>
